import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { UserInfo } from '@/models';

import { API_AUTH_URL, API_BUSINESS_URL } from '@/utils';

export interface ForgotPasswordRequest {
  email: string;
}

export interface ForgotPasswordResponse {
  userId: string;
  phone: string;
}

export interface SendPasswordResetOtpRequest {
  email: string;
}

const businessBaseQuery = fetchBaseQuery({ baseUrl: `${API_BUSINESS_URL}` });

const userService = createApi({
  reducerPath: 'userService',
  baseQuery: fetchBaseQuery({ baseUrl: `${API_AUTH_URL}` }),
  endpoints: (builder) => ({
    getUserInfo: builder.query<UserInfo, { accessToken: string }>({
      query: ({ accessToken }) => ({
        url: '/user/whoiam',
        method: 'GET',
        headers: {
          access_token: accessToken,
        },
      }),
    }),
    requestPasswordReset: builder.mutation<
      ForgotPasswordResponse,
      ForgotPasswordRequest
    >({
      queryFn: async ({ email }, api, extraOptions) => {
        const result = await businessBaseQuery(
          {
            url: '/users/forgot-password',
            method: 'POST',
            body: { email },
          },
          api,
          extraOptions
        );

        if (result.error) {
          return result;
        }

        return {
          data: result.data as ForgotPasswordResponse,
          meta: result.meta,
        };
      },
    }),
    resetPassword: builder.mutation<
      void,
      { userId: string; password: string; confirmPassword: string }
    >({
      queryFn: async (
        { userId, password, confirmPassword },
        api,
        extraOptions
      ) => {
        const result = await businessBaseQuery(
          {
            url: `/users/${userId}/reset-password`,
            method: 'PUT',
            body: { password, confirmPassword },
          },
          api,
          extraOptions
        );

        if (result.error) {
          return result;
        }

        return result as { data: void };
      },
    }),
    sendPasswordResetOtp: builder.mutation<void, SendPasswordResetOtpRequest>({
      queryFn: async ({ email }, api, extraOptions) => {
        const result = await businessBaseQuery(
          {
            url: '/user-otp/send-password-reset',
            method: 'POST',
            body: { email },
          },
          api,
          extraOptions
        );

        if (result.error) {
          return result;
        }

        return result as { data: void };
      },
    }),
  }),
});

export default userService;

export const {
  useGetUserInfoQuery,
  useRequestPasswordResetMutation,
  useResetPasswordMutation,
  useSendPasswordResetOtpMutation,
} = userService;
